import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react"; // Swiper is kept for the sliding effect, even if only one item is displayed
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// Assuming you pass the specific milestone data as the 'item' prop
export default function SleepCoachMilestoneModal({
  isOpen = false,
  setIsOpen,
  item = {}, // The single milestone object (e.g., {RankImage, title, description, unlockDate})
}) {
  const [showConfetti, setShowConfetti] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const hasItem = Object.keys(item).length > 0;

  useEffect(() => {
    if (isOpen && hasItem) {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 3000);
      return () => clearTimeout(timer);
    } else if (isOpen && !hasItem) {
      // Close if opened with no data
      setIsOpen(false);
    }
  }, [isOpen, hasItem, setIsOpen]);

  const confettiPieces = Array.from({ length: 50 }, (_, i) => i);

const formatDate = (dateString) => {
  const today = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  // Fallback label (translated or English if missing)
  const unlockedLabel = `${t("quizGame.Unlocked")} on`;

  // If text already contains "Unlocked on", return today's formatted text
  if (typeof dateString === "string" && dateString.includes("Unlocked on")) {
    return `${unlockedLabel} ${today}`;
  }

  // Normal formatting for actual date inputs
  if (!dateString) return t("quizGame.Unknown date");

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return `${unlockedLabel} ${today}`;
    
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch (error) {
    return `${unlockedLabel} ${today}`;
  }
};


  if (!isOpen || !hasItem) {
    return null;
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
          >
            {/* Confetti Animation */}
            {showConfetti && (
              <div className="absolute inset-0 pointer-events-none overflow-hidden">
                {confettiPieces.map((i) => (
                  <motion.div
                    key={i}
                    className="absolute w-3 h-3 rounded-sm"
                    style={{
                      backgroundColor: [
                        "#45B7D1",
                        "#6B7280",
                        "#4ECDC4",
                        "#4F46E5", // Indigo shades
                        "#6366F1",
                        "#3730A3",
                      ][i % 6],
                      left: `${Math.random() * 100}%`,
                      top: "-10px",
                    }}
                    initial={{ y: -100, rotation: 0, opacity: 1 }}
                    animate={{
                      y: window.innerHeight + 100,
                      rotation: Math.random() * 360,
                      opacity: 0,
                    }}
                    transition={{
                      duration: Math.random() * 6 + 4, // Slower movement (4 to 10 seconds)
                      delay: Math.random() * 3,
                      ease: "easeOut",
                    }}
                  />
                ))}
              </div>
            )}

            <motion.div
              className="relative bg-white rounded-3xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden flex flex-col"
              initial={{ scale: 0.3, opacity: 0, y: 100, rotateX: -15 }}
              animate={{ scale: 1, opacity: 1, y: 0, rotateX: 0 }}
              exit={{ scale: 0.3, opacity: 0, y: 100, rotateX: 15 }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 25,
                mass: 0.8,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setIsOpen(false)}
                className="absolute top-4 right-4 w-8 h-8 bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] rounded-full flex items-center justify-center hover:bg-gray-300 transition-colors text-white text-[23px] z-20"
              >
                ×
              </button>

              {/* Slide Content (using Swiper just for consistency, but only one slide) */}
              <div className="flex-grow h-[400px]">
                <Swiper slidesPerView={1} className="h-full">
                  <SwiperSlide>
                    <SleepCoachSlideContent item={item} formatDate={formatDate} />
                  </SwiperSlide>
                </Swiper>
              </div>

              {/* Button section */}
              {/* <div className="bg-white p-4">
                <motion.button
                  onClick={() => navigate("/sleep-coach-dashboard")} // Navigate to the Sleep Coach Dashboard
                  className="w-full px-6 py-3 bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] text-white rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 font-semibold cursor-pointer"
                >
                  {t("sleepCoachPreferences.View Sleep Dashboard")}
                </motion.button>
              </div> */}
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// ---------------------------------------------------------------------
// Dedicated Slide Content Component
// ---------------------------------------------------------------------

function SleepCoachSlideContent({ item, formatDate }) {
  const { t } = useTranslation();
  
  // Sleep Coach specific styling variables
  const colorGradient = "from-indigo-600 to-indigo-800";
  const badgeColor = "bg-indigo-400";
  const shadowStart = "rgba(79, 70, 229, 0.5)";
  const shadowEnd = "rgba(79, 70, 229, 0.8)";
  const badgeEmoji = "🌙";

  return (
    <div className="relative h-full p-8 text-center flex flex-col justify-center">
      <motion.div className={`absolute inset-0 bg-white opacity-10`} />

      {/* Floating particles (Indigo) */}
      <div className="absolute inset-0 pointer-events-none">
        {Array.from({ length: 15 }).map((_, i) => (
          <motion.div
            key={i}
            className={`absolute w-2 h-2 ${badgeColor} rounded-full opacity-60`}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -20, 0],
              opacity: [0.6, 1, 0.6],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 6 + Math.random() * 4,
              repeat: Infinity,
              delay: Math.random() * 4,
            }}
          />
        ))}
      </div>

      {item.RankImage && (
        <motion.div
          className="relative mx-auto mb-6"
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
        >
          <motion.div
            className={`w-24 h-24 bg-gradient-to-br ${colorGradient} rounded-full flex items-center justify-center shadow-2xl overflow-hidden`}
            animate={{
              boxShadow: [
                `0 0 20px ${shadowStart}`,
                `0 0 40px ${shadowEnd}`,
                `0 0 20px ${shadowStart}`,
              ],
            }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <img
              src={item.RankImage}
              alt={item.title || "Sleep Milestone"}
              className="w-16 h-16 object-cover rounded-full"
            />
          </motion.div>
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <motion.h2
          className={`text-3xl font-bold bg-gradient-to-r px-10 sm:px-0 ${colorGradient} bg-clip-text text-transparent mb-2`}
          animate={{ scale: [1, 1.05, 1] }}
          transition={{
            duration: 2,
            repeat: Infinity,
            repeatDelay: 1,
          }}
        >
          {t("sleepCoachPreferences.Milestone")}{" "}
          {t("quizGame.Unlocked")}! {badgeEmoji}
        </motion.h2>

        <motion.p
          className="text-xl font-semibold text-gray-700 mb-1"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
        >
          {item.title || t("sleepCoachPreferences.New Milestone Achieved")}
        </motion.p>

        <motion.p
          className="text-sm text-gray-500 mb-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9 }}
        >
          {/* {t("quizGame.Unlocked")} {t("quizGame.on")}{" "} */}
          {formatDate(item.unlockDate)}
        </motion.p>

        <motion.p
          className="text-sm text-gray-600 mb-6 px-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1 }}
        >
          {item.description || t("sleepCoachPreferences.Congratulations!")}
        </motion.p>
      </motion.div>
    </div>
  );
}